import * as THREE from 'three';
import {
  ANIM_BEGIN_NORMAL,
  ANIM_BEGIN_VERTEX,
  ANIM_FLOATS,
  ANIM_VERTEX_PARS,
  packAnim,
} from './anim';
import type { Part, Slot } from './bake';
import { glowMaterial, translucent } from './materials';

/**
 * Every entity's model drawn through shared InstancedMeshes: one per
 * (geometry, material) across all entities, so draw calls follow the number
 * of distinct parts in use, not the number of entities.
 *
 * - A look (`type:level[:variant]`, see `ModelBatcher.tsx`) is baked once into
 *   parts (`bake.ts`) and handed over with `defineLook`. An entity whose look
 *   isn't baked yet waits (`wantedLooks` lists what to bake) and shows its old
 *   look meanwhile, if it had one.
 * - The owner's colour: accent parts share one white material tinted per
 *   instance (`instanceColor`); emissive isn't tinted that way, so glow parts
 *   use one material per player colour.
 * - Only entities whose transform changed are rewritten, and only the span
 *   of matrices rewritten is uploaded; removal swaps the last instance into
 *   the hole.
 * - See-through parts get their own buckets with `depthWrite: false`.
 * - Animated parts (`anim.ts`) move in the vertex shader from per-instance
 *   attributes, so they cost nothing per frame on the CPU.
 * - An entity's matrix may scale y (a construction site rising from the ground).
 */

interface Bucket {
  mesh: THREE.InstancedMesh;
  /** Instance index → its entity and part, for swap-remove. */
  owners: Array<{ entity: Entity; part: number }>;
  /** The per-instance anim data, for animated buckets (`ANIM_FLOATS` per instance). */
  anim: THREE.InstancedBufferAttribute[] | null;
  /** Instance matrices rewritten since the last upload: indices `lo`–`hi` (none when `hi < lo`). */
  lo: number;
  hi: number;
}

interface Entity {
  id: string;
  look: string;
  parts: Part[];
  /** Per part: its bucket and instance index. */
  slots: Array<{ bucket: Bucket; index: number }>;
  color: THREE.Color;
  matrix: THREE.Matrix4;
  /** Seconds added to the clock for this entity's animations, so neighbours don't move in step. */
  timeOffset: number;
}

const ANIM_ATTRIBUTES = ['aAnimA', 'aAnimB', 'aAnimC', 'aAnimD'] as const;
const scratch = new THREE.Matrix4();
const scratchColor = new THREE.Color();
const animScratch = new Float32Array(ANIM_FLOATS);

/** A stable spread of animation start times from an entity id (0–60 s). */
function timeOffsetOf(id: string): number {
  let h = 2166136261;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return ((h >>> 0) % 60000) / 1000;
}

export class ModelBatcher {
  /** Add this to the scene; it holds every bucket's mesh. */
  readonly group = new THREE.Group();
  private readonly looks = new Map<string, Part[]>();
  private readonly wanted = new Set<string>();
  private readonly buckets = new Map<string, Bucket>();
  private readonly entities = new Map<string, Entity>();
  /** Entities waiting on a look's bake: what to show once it's ready. */
  private readonly pending = new Map<
    string,
    { look: string; matrix: THREE.Matrix4; color: THREE.Color }
  >();
  private readonly dirty = new Set<Entity>();
  private readonly touched = new Set<Bucket>();
  private readonly accent = new THREE.MeshStandardMaterial({ color: '#ffffff', flatShading: true });
  private readonly glows = new Map<number, THREE.Material>();
  private readonly seeThrough = new Map<THREE.Material, THREE.Material>();
  private readonly animatedMaterials = new Map<THREE.Material, THREE.Material>();
  private readonly time = { value: 0 };

  constructor(private readonly capacityStep = 32) {
    this.group.name = 'ModelBatcher';
  }

  /** Whether `look` has been baked. */
  hasLook(look: string): boolean {
    return this.looks.has(look);
  }

  /** Looks entities are waiting on (or asked for with `want`) that aren't baked yet. */
  wantedLooks(): string[] {
    return [...this.wanted];
  }

  /** How many looks `wantedLooks` would return, without allocating. */
  get wantedCount(): number {
    return this.wanted.size;
  }

  /** Ask for a look to be baked ahead of use (idle pre-baking). */
  want(look: string): void {
    if (!this.looks.has(look)) this.wanted.add(look);
  }

  /** Hand over a look's baked parts; entities waiting on it appear. */
  defineLook(look: string, parts: Part[]): void {
    this.looks.set(look, parts);
    this.wanted.delete(look);
    for (const [id, p] of this.pending) {
      if (p.look !== look) continue;
      this.pending.delete(id);
      this.place(id, look, p.matrix, p.color);
    }
  }

  /**
   * Show entity `id` with `look` at `matrix` (model root → world) in its
   * owner's `color`. Cheap to call every frame: an unchanged entity costs a
   * few comparisons.
   */
  set(id: string, look: string, matrix: THREE.Matrix4, color: THREE.ColorRepresentation): void {
    const c = scratchColor.set(color);
    const e = this.entities.get(id);
    if (e && e.look === look) {
      this.pending.delete(id);
      if (!e.color.equals(c)) this.recolor(e, c);
      if (!e.matrix.equals(matrix)) {
        e.matrix.copy(matrix);
        this.dirty.add(e);
      }
      return;
    }
    if (!this.looks.has(look)) {
      // Not baked yet: keep whatever it showed until it is.
      const p = this.pending.get(id);
      if (p) {
        p.look = look;
        p.matrix.copy(matrix);
        p.color.copy(c);
      } else this.pending.set(id, { look, matrix: matrix.clone(), color: c.clone() });
      this.wanted.add(look);
      if (e) this.setMatrix(id, matrix);
      return;
    }
    this.place(id, look, matrix, c);
  }

  /** Move an entity already shown (or waiting on its look). */
  setMatrix(id: string, matrix: THREE.Matrix4): void {
    this.pending.get(id)?.matrix.copy(matrix);
    const e = this.entities.get(id);
    if (!e || e.matrix.equals(matrix)) return;
    e.matrix.copy(matrix);
    this.dirty.add(e);
  }

  remove(id: string): void {
    this.pending.delete(id);
    const e = this.entities.get(id);
    if (!e) return;
    this.removeInstances(e);
    this.entities.delete(id);
    this.dirty.delete(e);
  }

  /** Per frame: write moved entities, advance the animation clock. Returns instances written. */
  update(time: number): number {
    this.time.value = time;
    let written = 0;
    for (const e of this.dirty) {
      for (let i = 0; i < e.parts.length; i++) {
        const { bucket, index } = e.slots[i];
        bucket.mesh.setMatrixAt(index, scratch.multiplyMatrices(e.matrix, e.parts[i].matrix));
        this.touch(bucket, index);
        written++;
      }
    }
    this.dirty.clear();
    for (const b of this.touched) {
      const matrices = b.mesh.instanceMatrix;
      matrices.clearUpdateRanges();
      matrices.addUpdateRange(b.lo * 16, (b.hi - b.lo + 1) * 16);
      matrices.needsUpdate = true;
      b.lo = Number.POSITIVE_INFINITY;
      b.hi = -1;
    }
    this.touched.clear();
    return written;
  }

  /** Mark bucket `b`'s matrix `index` for the next upload. */
  private touch(b: Bucket, index: number): void {
    this.touched.add(b);
    if (index < b.lo) b.lo = index;
    if (index > b.hi) b.hi = index;
  }

  /** Draw calls this batcher costs: buckets with at least one instance. */
  drawCalls(): number {
    let n = 0;
    for (const b of this.buckets.values()) if (b.mesh.count > 0) n++;
    return n;
  }

  /** Instances drawn across all buckets. */
  instances(): number {
    let n = 0;
    for (const b of this.buckets.values()) n += b.mesh.count;
    return n;
  }

  /** Each part's instance matrix for entity `id` (tests, debugging). */
  worldMatrices(id: string): THREE.Matrix4[] {
    const e = this.entities.get(id);
    if (!e) return [];
    return e.slots.map(({ bucket, index }) => {
      const m = new THREE.Matrix4();
      bucket.mesh.getMatrixAt(index, m);
      return m;
    });
  }

  /** The meshes drawing entity `id`'s parts (tests, debugging). */
  meshesOf(id: string): THREE.InstancedMesh[] {
    return this.entities.get(id)?.slots.map((s) => s.bucket.mesh) ?? [];
  }

  dispose(): void {
    for (const b of this.buckets.values()) {
      this.group.remove(b.mesh);
      b.mesh.dispose();
      if (b.anim) b.mesh.geometry.dispose();
    }
    this.buckets.clear();
    this.entities.clear();
    this.pending.clear();
    this.dirty.clear();
    for (const m of this.animatedMaterials.values()) m.dispose();
    this.animatedMaterials.clear();
  }

  private place(id: string, look: string, matrix: THREE.Matrix4, color: THREE.Color): void {
    const old = this.entities.get(id);
    if (old) this.remove(id);
    const parts = this.looks.get(look);
    if (!parts) return;
    const e: Entity = {
      id,
      look,
      parts,
      slots: [],
      color: color.clone(),
      matrix: matrix.clone(),
      timeOffset: timeOffsetOf(id),
    };
    this.addInstances(e);
    this.entities.set(id, e);
    this.dirty.add(e);
  }

  private addInstances(e: Entity): void {
    e.slots = e.parts.map((part, i) => {
      const b = this.bucketFor(part, e.color);
      if (b.mesh.count >= b.mesh.instanceMatrix.count) this.grow(b);
      const index = b.mesh.count++;
      b.owners[index] = { entity: e, part: i };
      if (b.mesh.instanceColor) {
        b.mesh.setColorAt(index, e.color);
        b.mesh.instanceColor.needsUpdate = true;
      }
      if (b.anim && part.anim) {
        packAnim(part.anim, e.timeOffset, animScratch, 0);
        b.anim.forEach((attr, k) => {
          (attr.array as Float32Array).set(animScratch.subarray(k * 4, k * 4 + 4), index * 4);
          attr.needsUpdate = true;
        });
      }
      return { bucket: b, index };
    });
  }

  private removeInstances(e: Entity): void {
    // Highest index first per bucket, so an entity with several parts in one
    // bucket never swaps one of its own already-removed slots back in.
    const order = e.slots.map((s, part) => ({ ...s, part })).sort((a, b) => b.index - a.index);
    for (const { bucket: b, index } of order) {
      const last = --b.mesh.count;
      if (index !== last) {
        const moved = b.owners[last];
        copyInstance(b.mesh.instanceMatrix, last, index);
        this.touch(b, index);
        if (b.mesh.instanceColor) copyInstance(b.mesh.instanceColor, last, index);
        for (const attr of b.anim ?? []) copyInstance(attr, last, index);
        b.owners[index] = moved;
        moved.entity.slots[moved.part].index = index;
      }
      b.owners.length = last;
      if (b.mesh.instanceColor) b.mesh.instanceColor.needsUpdate = true;
      for (const attr of b.anim ?? []) attr.needsUpdate = true;
    }
    e.slots = [];
  }

  private recolor(e: Entity, color: THREE.Color): void {
    e.color.copy(color);
    if (e.parts.some((p) => p.slot.kind === 'glow')) {
      // Glow parts change material, so bucket: re-add every part.
      this.removeInstances(e);
      this.addInstances(e);
      this.dirty.add(e);
      return;
    }
    for (const { bucket, index } of e.slots) {
      if (!bucket.mesh.instanceColor) continue;
      bucket.mesh.setColorAt(index, color);
      bucket.mesh.instanceColor.needsUpdate = true;
    }
  }

  private materialFor(slot: Slot, color: THREE.Color): THREE.Material {
    switch (slot.kind) {
      case 'shared':
        return this.depthSafe(slot.material);
      case 'accent':
        return slot.opacity === undefined ? this.accent : translucent(this.accent, slot.opacity);
      case 'glow': {
        const hex = color.getHex();
        let glow = this.glows.get(hex);
        if (!glow) {
          glow = glowMaterial(`#${color.getHexString()}`);
          this.glows.set(hex, glow);
        }
        return slot.opacity === undefined ? glow : translucent(glow, slot.opacity);
      }
    }
  }

  /** See-through materials drawn instanced don't write depth (instances aren't sorted among themselves). */
  private depthSafe(material: THREE.Material): THREE.Material {
    if (!material.transparent || !material.depthWrite) return material;
    let copy = this.seeThrough.get(material);
    if (!copy) {
      copy = material.clone();
      copy.depthWrite = false;
      this.seeThrough.set(material, copy);
    }
    return copy;
  }

  /** A copy of `material` whose vertex shader runs the per-instance animation. */
  private animatedMaterial(material: THREE.Material): THREE.Material {
    let copy = this.animatedMaterials.get(material);
    if (!copy) {
      copy = material.clone();
      const time = this.time;
      copy.onBeforeCompile = (shader) => {
        shader.uniforms.uAnimTime = time;
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', `#include <common>\n${ANIM_VERTEX_PARS}`)
          .replace('#include <beginnormal_vertex>', ANIM_BEGIN_NORMAL)
          .replace('#include <begin_vertex>', ANIM_BEGIN_VERTEX);
      };
      copy.customProgramCacheKey = () => 'territile-anim';
      this.animatedMaterials.set(material, copy);
    }
    return copy;
  }

  private bucketFor(part: Part, color: THREE.Color): Bucket {
    const base = this.materialFor(part.slot, color);
    const animated = part.anim !== undefined;
    const key = `${part.geometry.uuid}|${base.uuid}|${animated ? 'anim' : ''}`;
    const existing = this.buckets.get(key);
    if (existing) return existing;
    const cap = this.capacityStep;
    // Animated buckets carry their own per-instance attributes, so they get
    // their own geometry object sharing the part's vertex buffers.
    const geometry = animated ? shareVertices(part.geometry) : part.geometry;
    const material = animated ? this.animatedMaterial(base) : base;
    const mesh = new THREE.InstancedMesh(geometry, material, cap);
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    if (part.slot.kind === 'accent') {
      mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
    }
    const anim = animated
      ? ANIM_ATTRIBUTES.map((name) => addAnimAttribute(geometry, name, cap))
      : null;
    const bucket: Bucket = { mesh, owners: [], anim, lo: Number.POSITIVE_INFINITY, hi: -1 };
    this.buckets.set(key, bucket);
    this.group.add(mesh);
    return bucket;
  }

  /** Double a bucket's capacity: a new mesh with the old instances copied over. */
  private grow(b: Bucket): void {
    const old = b.mesh;
    const cap = old.instanceMatrix.count * 2;
    const mesh = new THREE.InstancedMesh(old.geometry, old.material, cap);
    mesh.frustumCulled = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    (mesh.instanceMatrix.array as Float32Array).set(old.instanceMatrix.array as Float32Array);
    if (old.instanceColor) {
      mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3), 3);
      (mesh.instanceColor.array as Float32Array).set(old.instanceColor.array as Float32Array);
    }
    if (b.anim) {
      // The old attributes' GPU buffers are left to the GC with them: growth
      // doubles, so it happens a handful of times per bucket per match.
      b.anim = ANIM_ATTRIBUTES.map((name, k) => {
        const attr = addAnimAttribute(mesh.geometry, name, cap);
        (attr.array as Float32Array).set((b.anim as THREE.InstancedBufferAttribute[])[k].array);
        return attr;
      });
    }
    mesh.count = old.count;
    this.group.remove(old);
    old.dispose();
    this.group.add(mesh);
    b.mesh = mesh;
  }
}

/** A geometry object reusing `geometry`'s index and vertex attributes (and so their GPU buffers). */
function shareVertices(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const copy = new THREE.BufferGeometry();
  copy.setIndex(geometry.index);
  for (const [name, attr] of Object.entries(geometry.attributes)) copy.setAttribute(name, attr);
  for (const g of geometry.groups) copy.addGroup(g.start, g.count, g.materialIndex);
  copy.setDrawRange(geometry.drawRange.start, geometry.drawRange.count);
  return copy;
}

function addAnimAttribute(
  geometry: THREE.BufferGeometry,
  name: string,
  cap: number
): THREE.InstancedBufferAttribute {
  const attr = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
  geometry.setAttribute(name, attr);
  return attr;
}

function copyInstance(attr: THREE.BufferAttribute, from: number, to: number): void {
  const size = attr.itemSize;
  (attr.array as Float32Array).copyWithin(to * size, from * size, from * size + size);
}
